// Per-row equivalent of legacy `applyIndemnityPayoutToPhase`. Indemnity
// policies pay a fixed monthly benefit regardless of cost; excess
// payout above eligible cost accrues as surplus and is drained on later
// shortfall months.
//
// Two branches (one or the other taken per row — never both):
//   1. openSurplus > 0 AND this row's payout falls short of eligible cost
//      → drain surplus into the shortfall against the row's *full* cost
//        (not just eligible portion), capped at openSurplus
//   2. otherwise → accrue new surplus = max(payout − eligibleCost, 0)
//
// Reused across strategies for indemnity products (CM II first; Hybrid
// indemnity variants will plug in the same helper).

export interface ApplyIndemnitySurplusInput {
  openSurplus: number;
  indemnityPayout: number;
  // Dollars-equivalent of the row's benefit-eligible cost portion
  // (eligible days × inflated daily cost).
  eligibleCost: number;
  // Dollars-equivalent of the row's *full* effective care cost. Surplus
  // drains against this — legacy precedent: `phaseInflatedProfessionalShareCost`.
  fullCost: number;
}

export interface ApplyIndemnitySurplusResult {
  // Direct cost-coverage from this row's payout (before surplus drain).
  // Maps to legacy `phaseCostCoverage` per row → `rows[].flows.ltcCoveragePaid`.
  ltcCoveragePaid: number;
  // Total dollars-applied-to-cost (direct + drained surplus). Maps to
  // legacy `costCoverageAddedToTotal` per row → `rows[].flows.ltcPayoutToCost`.
  ltcPayoutToCost: number;
  surplusApplied: number;
  surplusAccrued: number;
  closeSurplus: number;
}

export function applyIndemnitySurplus(
  input: ApplyIndemnitySurplusInput,
): ApplyIndemnitySurplusResult {
  const { openSurplus, indemnityPayout, eligibleCost, fullCost } = input;
  const ltcCoveragePaid = Math.min(eligibleCost, indemnityPayout);
  const shortfall = eligibleCost - ltcCoveragePaid;

  let surplusApplied = 0;
  let surplusAccrued = 0;
  let ltcPayoutToCost = ltcCoveragePaid;

  if (openSurplus > 0 && shortfall > 0) {
    const remainingCostToCover = Math.max(fullCost - ltcCoveragePaid, 0);
    surplusApplied = Math.min(openSurplus, remainingCostToCover);
    ltcPayoutToCost = ltcCoveragePaid + surplusApplied;
  } else {
    surplusAccrued = Math.max(indemnityPayout - ltcCoveragePaid, 0);
  }

  return {
    ltcCoveragePaid,
    ltcPayoutToCost,
    surplusApplied,
    surplusAccrued,
    closeSurplus: openSurplus + surplusAccrued - surplusApplied,
  };
}
